import { useEffect } from "react";

/**
 * Préserve la position de scroll du canva Puck à l'ouverture d'un overlay
 * (drawer, Select Radix, DropdownMenu…).
 *
 * Diagnostic : les overlays Radix déclenchent un reflow → l'iframe du canva
 * passe transitoirement à `height: 0` (auto-zoom Puck via ResizeObserver) → le
 * conteneur scrollable se rétracte → `scrollTop` clampé à 0 (« le canva remonte
 * en haut »). On ré-affirme le scroll pendant la fenêtre de recalcul (~1,5 s).
 */
export function preserveCanvasScroll(): void {
  if (typeof document === "undefined") return;

  type Target = { get: () => number; set: (v: number) => void; top: number };
  const targets: Target[] = [];
  if (typeof window !== "undefined" && window.scrollY > 0) {
    targets.push({
      get: () => window.scrollY,
      set: (v) => window.scrollTo(window.scrollX, v),
      top: window.scrollY,
    });
  }
  document.querySelectorAll<HTMLElement>("*").forEach((el) => {
    if (el.scrollTop > 0) {
      targets.push({
        get: () => el.scrollTop,
        set: (v) => {
          el.scrollTop = v;
        },
        top: el.scrollTop,
      });
    }
  });
  if (targets.length === 0) return;

  const apply = () => {
    for (const t of targets) if (t.get() !== t.top) t.set(t.top);
  };

  if (typeof requestAnimationFrame === "undefined") {
    apply();
    return;
  }
  const deadline =
    (typeof performance !== "undefined" ? performance.now() : 0) + 1500;
  const loop = () => {
    apply();
    const now =
      typeof performance !== "undefined" ? performance.now() : deadline;
    if (now < deadline) requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
}

/** Appelle `preserveCanvasScroll` quand `open` passe à `true`. */
export function usePreserveCanvasScrollOnOpen(open: boolean): void {
  useEffect(() => {
    if (open) preserveCanvasScroll();
  }, [open]);
}
